import React, { useState } from 'react'
import { Table } from 'react-bootstrap'
import { formatoMoneda, formatoNumero } from '../../common/formato-dashboard'

const COLUMNAS = [
    { campo: 'nombreResponsable', titulo: 'Responsable', derecha: false },
    { campo: 'casosActivos', titulo: 'Casos activos', derecha: true },
    { campo: 'montoConvenidoActivo', titulo: 'Monto convenido activo (COP)', derecha: true }
]

// Paso "redondo" (1, 2, 5 x 10^k) para el eje del gráfico, con a lo sumo ~6 marcas
const pasoEje = (maximo) => {
    const objetivo = Math.max(maximo, 1) / 6
    const potencia = Math.pow(10, Math.floor(Math.log10(objetivo)))
    const candidato = [1, 2, 5, 10].map((m) => m * potencia).find((p) => p >= objetivo)
    return candidato || potencia * 10
}

const GraficoDistribucion = ({ filas }) => {
    if (filas.length === 0) return null
    const ascendente = [...filas].sort((a, b) => a.casosActivos - b.casosActivos)
    const maximo = Math.max(...ascendente.map((f) => f.casosActivos))
    const paso = pasoEje(maximo)
    const topeEje = Math.ceil(maximo / paso) * paso
    const marcas = []
    for (let v = 0; v <= topeEje; v += paso) marcas.push(v)

    // Más casos = barra más oscura (tonos del morado corporativo #4F517A)
    const colorBarra = (valor) => {
        const intensidad = maximo > 0 ? valor / maximo : 0
        const alpha = 0.45 + intensidad * 0.55
        return `rgba(79, 81, 122, ${alpha.toFixed(2)})`
    }

    return (
        <div className='mt-4'>
            <div
                className='text-uppercase fw-bold mb-3 px-3 py-2 rounded'
                style={{ fontSize: '0.75rem', letterSpacing: '0.04em', color: '#4F517A', backgroundColor: '#eceef6', borderLeft: '3px solid #4F517A' }}
            >
                Distribución de casos activos por responsable
            </div>
            {ascendente.map((f) => (
                <div key={f.idResponsable} className='d-flex align-items-center' style={{ minHeight: '2rem' }}>
                    <div className='text-end pe-2 small text-truncate' style={{ width: '14rem', flexShrink: 0 }} title={f.nombreResponsable}>
                        {f.nombreResponsable}
                    </div>
                    <div className='flex-grow-1 position-relative border-start border-bottom' style={{ height: '1.6rem' }}>
                        <div
                            title={`${f.nombreResponsable}: ${formatoNumero(f.casosActivos)}`}
                            style={{ width: `${(f.casosActivos / topeEje) * 100}%`, height: '100%', backgroundColor: colorBarra(f.casosActivos) }}
                        />
                    </div>
                </div>
            ))}
            <div className='d-flex'>
                <div style={{ width: '14rem', flexShrink: 0 }} />
                <div className='flex-grow-1 position-relative small text-muted' style={{ height: '1.4rem' }}>
                    {marcas.map((v) => (
                        <span key={v} className='position-absolute' style={{ left: `${(v / topeEje) * 100}%`, transform: 'translateX(-50%)' }}>
                            {v}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    )
}

const BloqueResponsables = ({ data }) => {
    const [orden, setOrden] = useState({ campo: 'casosActivos', asc: false })
    const sinResponsable = data?.sinResponsable
    const porResponsable = data?.porResponsable || []
    const filas = [...porResponsable].sort((a, b) => {
        const va = a[orden.campo]
        const vb = b[orden.campo]
        const cmp = typeof va === 'string' ? String(va).localeCompare(String(vb)) : Number(va) - Number(vb)
        return orden.asc ? cmp : -cmp
    })
    const totalCasos = porResponsable.reduce((acc, f) => acc + Number(f.casosActivos || 0), 0)
    const totalMonto = porResponsable.reduce((acc, f) => acc + Number(f.montoConvenidoActivo || 0), 0)

    const cambiarOrden = (campo) =>
        setOrden((prev) => ({ campo, asc: prev.campo === campo ? !prev.asc : false }))

    const sinDato = sinResponsable === null || sinResponsable === undefined
    const colorPill = sinDato ? '#6c757d' : sinResponsable > 0 ? '#dc3545' : '#198754'

    return (
        <div>
            <span className='d-inline-block rounded-pill text-white fw-semibold px-3 py-1 mb-3 small' style={{ backgroundColor: colorPill }}>
                Casos sin responsable asignado: {formatoNumero(sinResponsable)}
            </span>
            <Table hover size='sm' className='mb-0'>
                <thead style={{ backgroundColor: '#f4f5f8' }}>
                    <tr>
                        {COLUMNAS.map((c) => (
                            <th key={c.campo} className={c.derecha ? 'text-end' : ''} style={{ cursor: 'pointer' }} onClick={() => cambiarOrden(c.campo)}>
                                {c.titulo}{orden.campo === c.campo ? (orden.asc ? ' ▲' : ' ▼') : ''}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filas.length === 0 ? (
                        <tr><td colSpan={COLUMNAS.length} className='text-center text-muted'>No hay responsables con casos activos asignados.</td></tr>
                    ) : filas.map((f) => (
                        <tr key={f.idResponsable}>
                            <td>{f.nombreResponsable}</td>
                            <td className='text-end'>{formatoNumero(f.casosActivos)}</td>
                            <td className='text-end'>{formatoMoneda(f.montoConvenidoActivo)}</td>
                        </tr>
                    ))}
                </tbody>
                {filas.length > 0 && (
                    <tfoot style={{ backgroundColor: '#f4f5f8' }}>
                        <tr className='fw-bold'>
                            <td>Total asignados</td>
                            <td className='text-end'>{formatoNumero(totalCasos)}</td>
                            <td className='text-end'>{formatoMoneda(totalMonto)}</td>
                        </tr>
                    </tfoot>
                )}
            </Table>
            <GraficoDistribucion filas={porResponsable} />
        </div>
    )
}
export default BloqueResponsables
